<template lang="pug">
.resource-schedule-fields
  h2.mb-S Scheduling options
  BriqRadio(
    v-if="!resource.isResourceGroup()"
    :options="scheduleTypeOptions"
    v-model="scheduleType"
    label="Scheduling"
    help="Do products for this resource require scheduling?"
    required="required"
    )
  template(v-if="showScheduleOptions")
    BriqInput.mb-S(
      v-if="isVisible('Capacity')"
      :disabled="isFieldDisabled('Capacity')"
      data-test-id="capacity"
      label="Capacity"
      name="Capacity"
      v-model.number="resource.Capacity"
      required
      type="number"
      placeholder="0"
      step="1"
      help="When teams are used, this is the number of teams. When teams are not used, it is the number of visitors"
      :error="capacityError"
      @blur="vuelidate.resource.Capacity.$touch()"
      )
    BriqCheckbox.mb-0(
      v-if="isVisible('Exclusive')"
      label="Exclusive"
      name="Exclusive"
      v-model="resource.Exclusive"
      )
    BriqCheckbox.mb-S(
      v-if="isVisible('AllowOrderChanges')"
      :disabled="isFieldDisabled('AllowOrderChanges')"
      label="Allow shuffling"
      name="AllowOrderChanges"
      v-model="resource.AllowOrderChanges"
      )
    BriqInput.mb-S(
      v-if="isVisible('Priority')"
      :disabled="isFieldDisabled('Priority')"
      data-test-id="priority"
      label="Planboard ordering"
      name="Priority"
      v-model.number="resource.Priority"
      required
      type="number"
      placeholder="0"
      step="1"
      min="0"
      help="I.e. 1 is at the top of the planboard"
      :error="priorityError"
      @blur="vuelidate.resource.Priority.$touch()"
      )
    BriqInput.mb-S(
      v-if="isVisible('TimeslotMinutes')"
      :disabled="isFieldDisabled('TimeslotMinutes')"
      data-test-id="timeslot-minutes"
      label="Timeslot duration (minutes)"
      name="TimeslotMinutes"
      v-model="resource.TimeslotMinutes"
      type="number"
      placeholder="0"
      step="1"
      min="0"
      help="The default length of a timeslot, in minutes"
      )
    BriqCheckbox.mb-S(
      v-if="isVisible('IgnoreTimeslotDuration')"
      :disabled="isFieldDisabled('IgnoreTimeslotDuration')"
      label="Products ignore timeslot duration"
      name="IgnoreTimeslotDuration"
      v-model="resource.IgnoreTimeslotDuration"
      help="When set to true, products will not take the duration from the timeslot, so only products that have an explicit duration configured will be scheduled, others will not have times"
      )
    BriqDurationInput.mb-S(
      v-if="isVisible('OneTimeLeadIn')"
      :disabled="isFieldDisabled('OneTimeLeadIn')"
      label="One time lead-in"
      help="The lead-in (extra time) before a resource, the first time it appears in a booking. Notation: HH:mm:ss, i.e. 05:00 means 5 minutes"
      name="OneTimeLeadIn"
      v-model="resource.OneTimeLeadIn"
      data-test-id="one-time-lead-in"
      )
    BriqDurationInput.mb-S(
      v-if="isVisible('PermanentTimeLeadIn')"
      :disabled="isFieldDisabled('PermanentLeadIn')"
      label="Permanent lead-in"
      help="The lead-in (extra time) before a resource, every time it is in a booking (both onetime and permanent are applied to the first time a resource appears in a booking). Notation: HH:mm:ss, i.e. 05:00 means 5 minutes"
      name="PermanentLeadIn"
      v-model="resource.PermanentTimeLeadIn"
      data-test-id="permanent-time-lead-in"
      )
    BriqRadio(
      v-if="showResetTimeType"
      :disabled="isFieldDisabled('ResetTime')"
      label="Reset time type"
      :options="resetTimeTypeOptions"
      v-model="resetTimeType"
      data-test-id="reset-time-type-radio-button"
      )
    BriqDurationInput.mb-S(
      v-if="showResetTime"
      :disabled="isFieldDisabled('ResetTime')"
      label="Reset time"
      help="The time after a product finishes, during which the product capacity is not released yet (effectively temporary reducing the max capacity of a resource). Notation: HH:mm:ss, i.e. 05:00 means 5 minutes"
      name="ResetTime"
      v-model="resource.ResetTime"
      data-test-id="reset-time"
      )
</template>

<script>
import isFieldDisabled from './isFieldDisabled.js'
import BriqDurationInput from '@v/components/BriqDurationInput.vue'

export default {
  name: 'ResourceScheduleFields',
  components: { BriqDurationInput },
  props: {
    resource: { type: Object, required: true },
    resourceTypes: Array,
    vuelidate: { type: Object, required: true },
    isExternalResourceLinkVisible: Boolean,
  },
  data() {
    return {
      scheduleTypeOptions: {
        nonScheduled: 'Non-scheduled',
        scheduled: 'Scheduled',
      },
      resetTimeTypeOptions: {
        NotUsed: 'Not used',
        CompleteResource: 'Block full capacity during reset time',
        PerParticipant: 'Block used capacity during reset time',
      },
    }
  },
  computed: {
    showScheduleOptions() {
      return this.resource.isResourceGroup() ? true : this.resource.ShowOnScheduler
    },
    displayedFields() {
      const type = this.resource.ResourceType
      return this.resourceTypes.find(resourceType => resourceType.ResourceType == type)
        ?.ActiveFields
    },
    capacityError() {
      if (!this.vuelidate.resource.Capacity?.$error) return null
      return 'Please enter a capacity, greater than 0'
    },
    priorityError() {
      if (!this.vuelidate.resource.Priority?.$error) return null
      return 'Please enter a planboard ordering'
    },
    scheduleType: {
      get() {
        return this.resource.ShowOnScheduler ? 'scheduled' : 'nonScheduled'
      },
      set(v) {
        this.resource.ShowOnScheduler = v === 'scheduled'
      },
    },
    showResetTimeType() {
      return this.isVisible('ResetTime') && !this.isExternalResourceLinkVisible
    },
    showResetTime() {
      return this.showResetTimeType && this.resource.ResetTimeType !== 'NotUsed'
    },
    resetTimeType: {
      get() {
        return this.resource.ResetTimeType
      },
      set(v) {
        this.resource.ResetTimeType = v
        this.resource.ResetTime = null
      },
    },
  },
  methods: {
    isVisible(fieldName) {
      return this.displayedFields.includes(fieldName)
    },
    isFieldDisabled(fieldName) {
      if (this.disabled) return true
      return isFieldDisabled(this.resource.ResourceType)(fieldName)
    },
  },
  watch: {
    isExternalResourceLinkVisible: function(v) {
      this.resource.ResetTimeType = null
      this.resource.ResetTime = null
    },
  },
}
</script>

<style scoped lang="stylus"></style>
